import { Component, OnInit } from '@angular/core';
import { BuildingModel } from 'src/app/core/models/building.model';
import { BuildingService } from './building.service';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { BuildingCreateUpdateDeleteComponent } from './modals/building-create-update-delete/building-create-update-delete.component';
import { FeiColumn } from 'src/app/core/base/interfaces/fei-column.interface';
import { DataTableBase } from 'src/app/core/base/components/data-table-base/data-table-base';
import { CrudModeEnum } from 'src/app/core/base/enum/crud-mode.enum';
import { Router } from '@angular/router';
import { ENUM_PERMISSIONS } from '../../../../core/enums/permission.enum';
import { AuthService } from '../../../../core/services/auth.service';
import { BuildingStatusEnum, BuildingStatusTranslation } from '../../../../core/enums/building-status.enum';
import { BUILDING_FILTER_SCHEMA } from './building-filter/building-filter.schema';

@Component({
  selector: 'vex-building',
  templateUrl: './building.component.html',
  styleUrls: ['./building.component.scss'],
})
export class BuildingComponent extends DataTableBase<BuildingModel> implements OnInit {
  feisColumns: Array<FeiColumn> = [
    { label: 'ID', column: 'technicalIdentifier' },
    { label: 'Ville', column: 'addresses', propertyDisplay: 'mainCity' },
    { label: 'Libellé', column: 'label' },
    { label: 'Détention', column: 'detentionType', propertyDisplay: 'detentionType.label' },
    { label: 'Bailleur / Propriétaire', column: 'supplier', propertyDisplay: 'supplier.label' },
    { label: 'Type bail', column: 'leaseType', propertyDisplay: 'leaseType.label' },
    { label: 'PDL (électricité)', column: 'electricityPdl' },
    { label: 'Autre ID Energie', column: 'otherEnergyPdl' },
    { label: 'Bâtiment fonctionnel', column: 'isActive', type: 'boolean' },
    { label: 'Statut Predik', column: 'status' },
    {
      label: '',
      column: 'actions',
      canUpdate: false,
      canArchive: false,
    },
  ];

  entityName = 'Bâtiments';

  ENUM_PERMISSION = ENUM_PERMISSIONS;

  canShowArchivePermission = false;

  protected override filterSchema = BUILDING_FILTER_SCHEMA;

  constructor(
    public service: BuildingService,
    private dialog: MatDialog,
    private router: Router,
    public authService: AuthService,
  ) {
    super();
    this.displayedColumns = this.feisColumns.map(x => x.column);
  }

  protected override setupTable(): void {
    this.requiredFields = ['*'];

    this.subscription.add(
      this.authService.getCurrentUserPermissions$().subscribe(permissions => {
        this.canShowArchivePermission = permissions.includes(ENUM_PERMISSIONS.READ_BUILDING);
      }),
    );
  }

  // Flags d'enrichissement hors de this.filters : applyAdvancedFilter() le remplace à chaque application
  protected override buildFilters(): Record<string, unknown> {
    return { ...super.buildFilters(), isStatusNeeded: true, availableSurfaceNeeded: true, energyPdlNeeded: true };
  }

  createItem(): void {
    this.dialog
      .open(BuildingCreateUpdateDeleteComponent)
      .afterClosed()
      .subscribe((building: BuildingModel) => {
        if (building) {
          if (building._id) {
            this.router.navigateByUrl('/management/building/' + building._id).then();
          } else {
            this.initData();
          }
        }
      });
  }

  updateItem(element: BuildingModel): void {
    this.dialog
      .open(BuildingCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          mode: CrudModeEnum.Update,
        },
      })
      .afterClosed()
      .subscribe((building: BuildingModel) => {
        if (building) {
          this.initData();
        }
      });
  }

  deleteItem(element: BuildingModel) {
    this.dialog
      .open(BuildingCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          name: `${element.label}`,
          mode: CrudModeEnum.Delete,
        },
      })
      .afterClosed()
      .subscribe((building: BuildingModel) => {
        if (building) {
          this.initData();
        }
      });
  }

  importItems() {
    this.dialog
      .open(BuildingCreateUpdateDeleteComponent, {
        data: {
          mode: CrudModeEnum.Import,
        },
      })
      .afterClosed()
      .subscribe((building: BuildingModel) => {
        if (building) {
          this.initData();
        }
      });
  }

  protected override transformData(data: BuildingModel[]): BuildingModel[] {
    const transformed = data.map((building: BuildingModel) => {
      building.status = BuildingStatusTranslation.translate(building.status as BuildingStatusEnum);
      return building;
    });

    const buildingIds = transformed.map(b => b._id);
    if (buildingIds.length > 0) {
      const sub = this.service.addressService
        .findAll(1, buildingIds.length * 5, '_id', 'asc', undefined, { buildingIds, requiredFields: ['*'] })
        .subscribe(res => {
          const buildingAddressMap = new Map<string, any[]>();

          // Grouper les adresses par buildingId
          res.data.forEach(addr => {
            addr.buildingIds?.forEach(buildingId => {
              if (!buildingAddressMap.has(buildingId)) {
                buildingAddressMap.set(buildingId, []);
              }
              buildingAddressMap.get(buildingId).push(addr);
            });
          });

          transformed.forEach(b => {
            const addresses = buildingAddressMap.get(b._id);
            if (!addresses || addresses.length === 0) {
              b.isAttentionRequired = true;
              b.attentionRequieredTooltip = 'Aucune adresse associée';
              b['mainCity'] = '';
            } else {
              // Trouver l'adresse principale via mainAddressId
              if (b.mainAddressId) {
                const mainAddress = addresses.find(addr => addr._id === b.mainAddressId);
                b['mainCity'] = mainAddress?.city || '';
              } else {
                // Si pas de mainAddressId, utiliser la première adresse
                b['mainCity'] = addresses[0]?.city || '';
              }
            }
          });

          // La recherche est gérée entièrement côté backend (y compris les villes)
          // On ne fait que mettre à jour les données existantes avec mainCity pour l'affichage
          // Sans re-filtrer ni re-assigner dataSource.data pour éviter le double affichage
        });
      this.subscription.add(sub);
    }

    return transformed;
  }
}
